import { useCallback } from '@web-stories-wp/react';
import useInsertElement from '@web-stories-wp/story-editor/src/components/canvas/useInsertElement';
import { useExternalMedia } from '@src/web-stories-wp/story-editor/src/app/externalMedia';
import GeneralVideoRowContainer from '../mediaContainers/generalVideoRowContainer';
import { useMemo } from 'react';
import GeneralImageRowContainer from '../mediaContainers/generalImageRowContainer';
import createResourceFromExternalMedia from '../helpers/createResourceFromExternalMedia';
import { is3pMediaProvider } from '@src/web-stories-wp/story-editor/src/app/media/media3p/providerConfiguration';
import { useInsertLibraryMediaElement } from '@src/hooks';

export default function ExternalMediaRow({
  asset,
  providerType,
  lastElementRef,
  listContainerRef,
  enableDeletion,
  enableEditInAzzuu,
  isThumbnailBatchReady,
  onThumbnailReady,
  ...editMenuProps
}) {
  const {
    actions: { deleteMedia },
    state: { mediaType },
  } = useExternalMedia();

  const resolvedProviderType = providerType ?? mediaType.value;
  const is3pMedia = is3pMediaProvider(resolvedProviderType);

  const insertElement = useInsertElement();
  const { insertResource } = useInsertLibraryMediaElement();

  const onRowClicked = useCallback(async () => {
    const resource = asset.galleryOnly
      ? asset
      : await createResourceFromExternalMedia(asset);
    return is3pMedia
      ? insertResource(resource, asset.thumbnailUrl)
      : insertElement(resource.type, { resource });
  }, [asset, insertElement, insertResource, is3pMedia]);

  const isVideoAsset = useMemo(
    () => asset.metadata?.filetype?.includes('video'),
    [asset]
  );

  const handleOnDelete = useCallback(() => {
    if (is3pMedia) return;
    deleteMedia(asset.id);
  }, [asset.id, deleteMedia, is3pMedia]);

  const handleOnEditInAzzuu = useCallback(() => {
    if (is3pMedia) return;
    if (!asset.externalCallbackUrl) return;
    window.open(asset.externalCallbackUrl, '_blank');
  }, [asset.externalCallbackUrl, is3pMedia]);

  return isVideoAsset ? (
    <GeneralVideoRowContainer
      video={asset}
      onRowClicked={onRowClicked}
      lastElementRef={lastElementRef}
      listContainerRef={listContainerRef}
      providerType={resolvedProviderType}
      handleOnDelete={enableDeletion && !is3pMedia ? handleOnDelete : undefined}
      handleOnEditInAzzuu={
        enableEditInAzzuu && !is3pMedia && asset.externalCallbackUrl
          ? handleOnEditInAzzuu
          : undefined
      }
      isThumbnailBatchReady={isThumbnailBatchReady}
      onThumbnailReady={onThumbnailReady}
      {...editMenuProps}
    />
  ) : (
    <GeneralImageRowContainer
      asset={asset}
      onRowClicked={onRowClicked}
      lastElementRef={lastElementRef}
      providerType={resolvedProviderType}
      handleOnDelete={enableDeletion && !is3pMedia ? handleOnDelete : undefined}
      handleOnEditInAzzuu={
        enableEditInAzzuu && !is3pMedia && asset.externalCallbackUrl
          ? handleOnEditInAzzuu
          : undefined
      }
      isThumbnailBatchReady={isThumbnailBatchReady}
      onThumbnailReady={onThumbnailReady}
      {...editMenuProps}
    />
  );
}
